import {
  ChatMessage,
  ChatSession,
  CreateSessionRequest,
  SendMessageRequest,
  SendMessageResponse,
} from "@/types/chat";
import { apiClient } from "./apiClient";

class ChatService {
  private api = apiClient.instance;

  /**
   * Cria uma nova sessão de chat
   */
  async createSession(request: CreateSessionRequest): Promise<ChatSession> {
    try {
      const response = await this.api.post("/api/chat/session", request);
      return response.data;
    } catch (error) {
      throw new Error("Falha ao criar sessão de chat");
    }
  }

  /**
   * Envia uma mensagem para o chat
   */
  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    try {
      // Usar timeout maior para requisições de chat (podem demorar mais)
      const response = await this.api.post(
        `/api/chat/message`,
        {
          sessionId: request.sessionId,
          message: request.content,
        },
        {
          timeout: 120000, // 2 minutos para processamento de IA
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Erro ao enviar mensagem:", error);

      // Tratamento específico para erros de cancelamento
      if (error.code === "ECONNABORTED" || error.message?.includes("canceled")) {
        throw new Error("Requisição cancelada ou timeout. Tente novamente.");
      }

      // Tratamento para erros de rede
      if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
        throw new Error("Erro de conexão. Verifique sua internet e tente novamente.");
      }

      // Tratamento para erros de resposta do servidor
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error || "Erro desconhecido";
        throw new Error(`Erro ${status}: ${message}`);
      }

      // Erro genérico
      throw new Error(error.message || "Falha ao enviar mensagem");
    }
  }

  /**
   * Obtém o histórico de mensagens de uma sessão
   */
  async getMessageHistory(sessionId: string): Promise<ChatMessage[]> {
    try {
      const response = await this.api.get(`/api/chat/history/${sessionId}`);
      return response.data;
    } catch (error) {
      throw new Error("Falha ao carregar histórico de mensagens");
    }
  }

  /**
   * Obtém informações de uma sessão
   */
  async getSession(sessionId: string): Promise<ChatSession> {
    try {
      const response = await this.api.get(`/api/chat/session/${sessionId}`);
      return response.data;
    } catch (error) {
      throw new Error("Falha ao carregar informações da sessão");
    }
  }

  /**
   * Lista todas as sessões do usuário
   */
  async listSessions(): Promise<ChatSession[]> {
    try {
      const response = await this.api.get("/api/chat/session");
      return response.data;
    } catch (error) {
      throw new Error("Falha ao carregar lista de sessões");
    }
  }

  /**
   * Deleta uma sessão
   */
  async deleteSession(sessionId: string): Promise<void> {
    try {
      await this.api.delete(`/api/chat/session/${sessionId}`);
    } catch (error) {
      throw new Error("Falha ao deletar sessão");
    }
  }

  /**
   * Verifica o status de conexão com o servidor
   */
  async checkConnection(): Promise<boolean> {
    try {
      await this.api.get("/api/health");
      return true;
    } catch (error) {
      return false;
    }
  }
}

export const chatService = new ChatService();
